import { Component } from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@homy/ui";
import { AlertCircle } from "lucide-react";
import i18n from "@/i18n";

export class ModalErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    try {
      Sentry.captureException(error, { extra: errorInfo });
    } catch {
      // Prevent failure in testing environments without Sentry configured
    }
    console.error("[ModalErrorBoundary] Caught modal exception:", error, errorInfo);
  }

  handleDismiss = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (typeof this.props.onClose === "function") {
      this.props.onClose();
    }
  };

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        if (typeof this.props.fallback === "function") {
          return this.props.fallback({
            error: this.state.error,
            resetErrorBoundary: this.reset,
          });
        }
        return this.props.fallback;
      }

      const title =
        this.props.title ||
        i18n.t("common:errorBoundary.modalTitle", "Dialog Error");
      const message =
        this.props.message ||
        i18n.t(
          "common:errorBoundary.modalMessage",
          "An unexpected error occurred inside this dialog.",
        );
      const closeLabel = i18n.t("common:actions.close", "Close");

      return (
        <Dialog open onOpenChange={(open) => !open && this.handleDismiss()}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <span>{title}</span>
              </DialogTitle>
              <DialogDescription>{message}</DialogDescription>
            </DialogHeader>
            {this.state.error && (
              <div className="p-3 my-2 rounded-md bg-muted text-muted-foreground text-xs font-mono overflow-auto max-h-28">
                {this.state.error.message || String(this.state.error)}
              </div>
            )}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={this.handleDismiss}
                className="min-h-[44px] sm:min-h-0"
              >
                {closeLabel}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    }

    return this.props.children;
  }
}

ModalErrorBoundary.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func,
  title: PropTypes.string,
  message: PropTypes.string,
  fallback: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default ModalErrorBoundary;
